import { ANALYTICS_HEAP_IDS, formatHeapId } from '@/common/constants';
import { CTA, CTAProps } from '@/components/CTA';
import {
  ArrowChevronDownIcon,
  ArrowChevronLeftIcon,
  ArrowChevronRightIcon,
  ArrowChevronUpIcon,
} from '@eventbrite/marmalade/icons';
import classNames from 'classnames';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavbarExpansionItemProp, NavbarExpansionLinkProp, NavbarItemProp } from '../../../types';
import styles from './Expansion.module.scss';

const SectionLinks: React.FC<{
  sectionName: string;
  sectionLinks: NavbarExpansionLinkProp[];
  onNavigate?: () => void;
}> = ({ sectionName, sectionLinks, onNavigate }) => {
  return (
    <div className={styles.section_links}>
      {sectionLinks.map((item, index) => (
        <a
          key={item.label + index}
          href={item.link}
          onClick={onNavigate}
          data-heap-id={formatHeapId([
            ANALYTICS_HEAP_IDS.MOBILE,
            ANALYTICS_HEAP_IDS.NAVBAR_EXPANSION,
            sectionName,
            item.label,
          ])}
        >
          {item.label}
        </a>
      ))}
    </div>
  );
};
interface ExpansionLevel2Props {
  title: string;
  expansionItems: NavbarExpansionItemProp[];
  isT3Treatment?: boolean;
  onNavigate?: () => void;
}

const ExpansionLevel2: React.FC<ExpansionLevel2Props> = ({
  title,
  expansionItems,
  isT3Treatment = false,
  onNavigate,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const handleShowSectionLinks = (item: NavbarExpansionItemProp) => {
    if (expandedSection && expandedSection === item.sectionName) {
      setExpandedSection(null);
      return;
    }
    setExpandedSection(item.sectionName);
  };

  return (
    <div
      className={classNames(styles.expansion_level_2, isT3Treatment && styles.treatment_level_2)}
    >
      <div className={styles.content}>
        <span className={styles.title}>{title}</span>
        <nav aria-label="Mobile navigation level 2" className={styles.sections}>
          {expansionItems.map((item, index) => {
            const isExpanded = expandedSection === item.sectionName;
            return (
              <button
                aria-haspopup="true"
                aria-expanded={isExpanded}
                key={item.sectionName + index}
                onClick={() => handleShowSectionLinks(item)}
                data-heap-id={formatHeapId([
                  ANALYTICS_HEAP_IDS.MOBILE,
                  ANALYTICS_HEAP_IDS.NAVBAR_EXPANSION,
                  item.sectionName,
                ])}
              >
                <div className={styles.section_item}>
                  <span>{item.sectionName}</span>
                  {isExpanded ? (
                    <ArrowChevronUpIcon size="MD" color="var(--marm-color-action-fg-base)" />
                  ) : (
                    <ArrowChevronDownIcon size="MD" color="var(--marm-color-action-fg-base)" />
                  )}
                </div>
                {isExpanded && (
                  <SectionLinks
                    sectionName={item.sectionName}
                    sectionLinks={item.expansionLinks}
                    onNavigate={onNavigate}
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

const BackAction: React.FC<{ onClick: () => void; isT3Treatment?: boolean }> = ({
  onClick,
  isT3Treatment = false,
}) => {
  return (
    <div
      className={classNames(styles.back_action, isT3Treatment && styles.treatment_back_action)}
      onClick={onClick}
    >
      <button className={styles.back_action_content}>
        <ArrowChevronLeftIcon size="SM" color="var(--marm-color-action-fg-base)" />
        <span>
          <FormattedMessage
            id="Back"
            defaultMessage="Back"
            description="Button text for the back button on navbar expansion"
          />
        </span>
      </button>
    </div>
  );
};

export interface ExpansionProps {
  items?: NavbarItemProp[];
  ctas?: CTAProps[];
  isT3Treatment?: boolean;
  onNavigate?: () => void;
}

export const Expansion: React.FC<ExpansionProps> = ({
  items,
  ctas,
  isT3Treatment = false,
  onNavigate,
}) => {
  const [expandedLevel2Item, setExpandedLevel2Item] = useState<NavbarItemProp | null>(null);

  const handleShowExpansionLevel2 = (item: NavbarItemProp) => {
    if (expandedLevel2Item && expandedLevel2Item.title === item.title) {
      setExpandedLevel2Item(null);
      return;
    }
    setExpandedLevel2Item(item);
  };

  const handleBackAction = () => {
    setExpandedLevel2Item(null);
  };

  return (
    <>
      {expandedLevel2Item && (
        <BackAction onClick={handleBackAction} isT3Treatment={isT3Treatment} />
      )}
      {!expandedLevel2Item && (
        <nav
          aria-label="Mobile Main navigation"
          className={classNames(styles.expansion, isT3Treatment && styles.treatment_expansion)}
        >
          {(items || []).map((item, index) =>
            item.type === 'NavitemExpansionV2' ? (
              <button
                className={styles.expansion_item}
                aria-haspopup="true"
                onClick={() => handleShowExpansionLevel2(item)}
                key={item.title + index}
                data-heap-id={formatHeapId([
                  ANALYTICS_HEAP_IDS.MOBILE,
                  ANALYTICS_HEAP_IDS.NAVBAR,
                  item.title,
                ])}
              >
                <span>{item.title}</span>
                <ArrowChevronRightIcon size="MD" color="var(--marm-color-action-fg-base)" />
              </button>
            ) : (
              <a
                className={styles.expansion_item_link}
                href={item.expansionItems[0].expansionLinks[0].link}
                onClick={onNavigate}
                key={item.title + index}
                data-heap-id={formatHeapId([
                  ANALYTICS_HEAP_IDS.MOBILE,
                  ANALYTICS_HEAP_IDS.NAVBAR,
                  item.title,
                ])}
              >
                {item.title}
              </a>
            ),
          )}
        </nav>
      )}
      {expandedLevel2Item && (
        <ExpansionLevel2
          key={expandedLevel2Item.title}
          title={expandedLevel2Item.title}
          expansionItems={expandedLevel2Item.expansionItems}
          isT3Treatment={isT3Treatment}
          onNavigate={onNavigate}
        />
      )}
      {ctas && (
        <div
          className={classNames(
            styles.expansion_ctas,
            isT3Treatment && styles.treatment_expansion_ctas,
          )}
          onClick={onNavigate}
        >
          {ctas.map((cta, index) => (
            <CTA {...cta} key={cta.label + index} />
          ))}
        </div>
      )}
    </>
  );
};
